import { Address } from '../types/common';

export const formatAddress = (address: Address): string => {
  return `${address.street}, ${address.postalCode} ${address.city}, ${address.country}`;
};

export const generateQuotationNumber = (
  lastQuotationNumber?: string, 
  customerShortName?: string,
  allQuotations: any[] = [], // Array of all quotations to track company-specific numbering
  customerId?: string, // Customer ID to track same-company sequences
  documentType?: 'quotation' | 'contract', // Document type to determine if it's an offer (A) or contract (S)
  customerCode?: string, // 4 jegyű egyedi ügyfélazonosító (pl: 0001)
  allCustomers: any[] = [] // Az összes ügyfél a customerCode generálásához
): string => {
  // Először eltávolítjuk az ampersand (&) jelet a lastQuotationNumber-ből, ha van
  let cleanedLastQuotationNumber = lastQuotationNumber;
  if (cleanedLastQuotationNumber) {
    cleanedLastQuotationNumber = cleanedLastQuotationNumber.replace(/&/g, '');
    if (cleanedLastQuotationNumber !== lastQuotationNumber) {
      console.log('Original existing quotation number:', lastQuotationNumber);
      console.log('Cleaned existing quotation number:', cleanedLastQuotationNumber);
    }
  }
  
  // A függvény kimenete
  let finalResult: string;
  
  // Ha van már érvényes ajánlati szám (nem minta), és szerkesztünk, akkor azt adjuk vissza
  if (cleanedLastQuotationNumber && !cleanedLastQuotationNumber.includes('-Minta')) {
    // Csak akkor adjuk vissza a tisztított sorszámot, ha nem új ajánlatot generálunk
    // Ha új ajánlatot generálunk, akkor mindig új sorszámot kell adnunk
    if (allQuotations.length === 0 || !customerId) {
      finalResult = cleanedLastQuotationNumber;
      console.log('RETURNING EXISTING QUOTATION NUMBER:', finalResult);
      return finalResult;
    }
  }

  const today = new Date();
  const year = today.getFullYear();
  const yearShort = year.toString().slice(-2); // Get just the last two digits (e.g., 25 for 2025)
  
  // Típus: A = ajánlat, S = szerződés (SZ helyett mostantól S)
  const type = documentType === 'contract' ? "S" : "A"; // S = szerződés, A = ajánlat

  // Rövid cégnév, ha nincs megadva, akkor üres string
  const shortName = customerShortName || "";
  
  // Eltávolítjuk az & jelet a névből, hogy ne okozzon problémát az XML-ben
  const shortNameWithoutAmpersand = shortName.replace(/&/g, '');

  // Új formátum: E.I.-VE-0001-03-A-25-OerlikoEldim
  // XXXX = 4 jegyű egyedi ügyfélazonosító (0001, 0002, stb.) - ez nem változik évente sem
  // YY = 2 jegyű folyamatos sorszám az ügyfélnek összesen hányadik ajánlata (nem évente újrainduló!)
  // Z = A/S (ajánlat/szerződés)
  
  // Ügyfélazonosító meghatározása
  let finalCustomerCode = customerCode;
  if (!finalCustomerCode) {
    console.warn('Customer does not have a customerCode yet, generating one automatically');
    // Ha nincs customerCode, automatikusan generálunk egyet
    // Ez régi ügyfelek esetén fordulhat elő, akik még az új rendszer előtt jöttek létre
    finalCustomerCode = generateCustomerCode(allCustomers); // Alapértelmezett üres tömbből generál
    console.log('Generated automatic customerCode for legacy customer:', finalCustomerCode);
  }
  
     // Az ügyfélnek összesen hányadik ajánlata/szerződése ez (nem évente újrainduló!)
   let continuousSequence = 1;
  
   // Ellenőrizzük, hogy van-e már ajánlat/szerződés a rendszerben ennél az ügyféltől
   if (allQuotations.length > 0 && customerId) {
    try {
       console.log('Calculating quotation number with new system:', {
        lastQuotationNumber: cleanedLastQuotationNumber,
        customerShortName,
        customerId,
         customerCode: finalCustomerCode,
        totalQuotations: allQuotations.length
      });
      
       // Az ügyfél összes ajánlatának/szerződésének lekérése (minden évből!)
       const customerQuotationsAll = allQuotations
         .filter(q => q.customerId === customerId)
        .filter(q => q.quotationNumber || q.number)
        .map(q => q.quotationNumber || q.number)
        .filter(Boolean);
      
       console.log('All customer quotations ever:', customerQuotationsAll);
      
       if (customerQuotationsAll.length > 0) {
         // Keressük meg a legmagasabb folyamatos sorszámot az összes évből
         const continuousSequences = customerQuotationsAll.map(num => {
        const parts = num.split('-');
           if (parts.length >= 6) {
             // Új formátum: E.I.-VE-0001-03-A-25-OerlikoEldim
             return parseInt(parts[3], 10); // YY rész (folyamatos sorszám)
           } else if (parts.length >= 5) {
             // Régi formátum visszakompatibilitás: E.I.-VE-0013-A-25-HIHInves
             // Itt a 4. karakter volt a verzió, de az új rendszerben ez 01-ként indul
             return 1;
          }
          return 0;
         }).filter(n => !isNaN(n) && n > 0);
        
         console.log('All continuous sequences found:', continuousSequences);
        
         if (continuousSequences.length > 0) {
           continuousSequence = Math.max(...continuousSequences) + 1;
           console.log('Next continuous sequence:', continuousSequence);
          }
        }
      
    } catch (error) {
      console.error('Error calculating quotation number with new system:', error);
              }
     }
   
   // Formázzuk a folyamatos sorszámot 2 jegyűre
   const formattedContinuousSequence = continuousSequence.toString().padStart(2, '0');
   
   console.log('Final new format parameters:', {
     customerCode: finalCustomerCode,
     continuousSequence: formattedContinuousSequence,
     type,
     year: yearShort,
     shortName: shortNameWithoutAmpersand
   });
      
   // Végleges formátum összeállítása: E.I.-VE-0001-03-A-25-OerlikoEldim
   let result = `E.I.-VE-${finalCustomerCode}-${formattedContinuousSequence}-${type}-${yearShort}-${shortNameWithoutAmpersand}`;
      
      // Utolsó ellenőrzés - ha még mindig lenne benne ampersand, távolítsuk el
      if (result.includes('&')) {
        console.log('Removing remaining ampersands from result:', result);
        result = result.replace(/&/g, '');
      }
      
      // Ellenőrizzük, hogy a generált szám nem ütközik-e már létező számmal
      if (allQuotations.length > 0) {
        const existingNumbers = allQuotations
          .filter(q => q.quotationNumber || q.number)
          .map(q => q.quotationNumber || q.number)
          .filter(Boolean);
          
     // Ha a generált szám már létezik, növeljük a folyamatos sorszámot
     while (existingNumbers.includes(result)) {
          console.log('DUPLICATE DETECTED! Generated number already exists:', result);
       continuousSequence += 1;
       const newFormattedContinuousSequence = continuousSequence.toString().padStart(2, '0');
       result = `E.I.-VE-${finalCustomerCode}-${newFormattedContinuousSequence}-${type}-${yearShort}-${shortNameWithoutAmpersand}`;
          console.log('Re-generated quotation number to avoid duplicate:', result);
          
       // Biztonság kedvéért megállunk 99-nél
       if (continuousSequence >= 99) {
         console.error('Continuous sequence reached maximum (99) for customer:', finalCustomerCode);
         break;
       }
        }
   }
  
  console.log('Generated quotation number with new system:', result);
  finalResult = result;
  return finalResult;
};

// Segédfüggvény a 4 jegyű egyedi ügyfélazonosító generálásához
export const generateCustomerCode = (allCustomers: any[] = []): string => {
  // Kinyerjük az összes meglévő customerCode-ot
  const existingCodes = allCustomers
    .filter(customer => customer.customerCode)
    .map(customer => customer.customerCode)
    .filter(Boolean)
    .map(code => parseInt(code, 10))
    .filter(n => !isNaN(n) && n > 0);

  console.log('Existing customer codes:', existingCodes);

  // Ha nincs még kód, 0001-gyel kezdjük
  if (existingCodes.length === 0) {
    return '0001';
  }

  // Rendezzük a kódokat és keressük meg az első hiányzó számot
  const sortedCodes = Array.from(new Set(existingCodes)).sort((a, b) => a - b);
  
  // Keressük meg az első hiányzó számot a sorozatban
  for (let i = 0; i < sortedCodes.length; i++) {
    const expectedCode = i + 1;
    if (sortedCodes[i] !== expectedCode) {
      // Találtunk egy hiányzó számot
      return expectedCode.toString().padStart(4, '0');
    }
  }

  // Ha nincs hiányzó szám, akkor a következő szám a legnagyobb + 1
  const maxCode = Math.max(...sortedCodes);
  const nextCode = maxCode + 1;

  // Formázzuk 4 jegyűre
  return nextCode.toString().padStart(4, '0');
};

// Segédfüggvény a dokumentum dátumának formázásához
export const formatDocumentDate = (date: any): string => {
  try {
    if (!date) return '-';

    // Ha Timestamp objektum
    if (date && typeof date.toDate === 'function') {
      const d = date.toDate();
      return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}.`;
    }

    // Ha Date objektum
    if (date instanceof Date) {
      return `${date.getFullYear()}.${String(date.getMonth() + 1).padStart(2, '0')}.${String(date.getDate()).padStart(2, '0')}.`;
    }

    // Ha szám (pl. timestamp)
    if (typeof date === 'number') {
      const d = new Date(date);
      return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}.`;
    }

    // Ha objektum seconds mezővel (Firebase Timestamp-szerű objektum)
    if (typeof date === 'object' && date !== null && 'seconds' in date) {
      const d = new Date(date.seconds * 1000);
      return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}.`;
    }

    // Utolsó próbálkozás - konvertálás számra
    const d = new Date(Number(date));
    return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}.`;
  } catch (error) {
    return '-';
  }
};